import { useEffect, useRef, useState } from "preact/hooks";

export function GalleryImage({ alt, fullSrc, fallbackSrc, telemetry, failureReason }) {
  const [photo, setPhoto] = useState({ src: "", previous: "", loading: true, failed: false });
  const [attempt, setAttempt] = useState(0);
  const reportedSrc = useRef("");
  // A ref, so a new function identity never restarts the load.
  const reasonOf = useRef();
  reasonOf.current = failureReason;

  useEffect(() => {
    let active = true;
    let transitionTimer;
    const image = new Image();
    image.decoding = "async";
    // Each photo counts once, however often its load is retried. `extra` may be a promise of properties.
    const report = (outcome, extra) => {
      if (reportedSrc.current === fullSrc) return;
      reportedSrc.current = fullSrc;
      Promise.resolve(extra)
        .catch(() => undefined)
        .then((props) => telemetry.count("media_viewed", { kind: "photo", outcome, media_surface: "gallery", ...props }));
    };
    setPhoto((current) => ({ ...current, previous: "", loading: true, failed: false }));
    image.onload = async () => {
      // Decode before swapping so slow media never flashes a blank frame.
      await image.decode().catch(() => {});
      if (!active) return;
      report("ok");
      setPhoto((current) => ({ src: image.src, previous: current.src, loading: false, failed: false }));
      transitionTimer = window.setTimeout(() => {
        if (active) setPhoto((current) => ({ ...current, previous: "" }));
      }, 200);
    };
    let triedFallback = false;
    image.onerror = () => {
      if (!active) return;
      if (!triedFallback && fallbackSrc && fallbackSrc !== fullSrc) {
        triedFallback = true;
        image.src = fallbackSrc;
      } else {
        const failedSrc = triedFallback ? fallbackSrc : fullSrc;
        report("error", Promise.resolve(reasonOf.current ? reasonOf.current(failedSrc) : null)
          .catch(() => null)
          .then((reason) => ({ reason: reason ?? "unsupported_format" })));
        setPhoto({ src: "", previous: "", loading: false, failed: true });
      }
    };
    image.src = fullSrc;
    return () => {
      active = false;
      image.onload = null;
      image.onerror = null;
      window.clearTimeout(transitionTimer);
    };
  }, [fullSrc, fallbackSrc, attempt]);

  return (
    <div className="gallery-photo" aria-busy={photo.loading}>
      {photo.previous && photo.previous !== photo.src ? (
        <img className="gallery-image-previous" src={photo.previous} alt="" aria-hidden="true" draggable={false} />
      ) : null}
      {photo.src ? <img key={photo.src} className="gallery-image" src={photo.src} alt={photo.loading ? "" : alt} draggable={false} /> : null}
      {photo.loading ? <p className="gallery-photo-status" role="status">Opening photo…</p> : null}
      {photo.failed ? (
        <div className="gallery-photo-error" role="status">
          <p>This photo couldn’t load.</p>
          <button className="primary-button" type="button" onClick={() => setAttempt((value) => value + 1)}>Try again</button>
        </div>
      ) : null}
    </div>
  );
}
